
import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import {ArrowLeft, MapPin, BarChart3, Droplets, Info, Search, Filter} from 'lucide-react'

const Platform = () => {
  const navigate = useNavigate()
  const [selectedCuenca, setSelectedCuenca] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')

  // Datos simulados de cuencas importantes de Chiapas
  const cuencas = [
    {
      id: '1',
      nombre: 'Río Grijalva',
      jerarquia: 1,
      area: '52,000 km²',
      importancia: 'Muy Alta',
      municipios: 45,
      poblacion: '2.1M habitantes',
      color: '#dc2626',
      descripcion: 'Principal cuenca hidrográfica del estado, vital para el desarrollo económico y social.'
    },
    {
      id: '2',
      nombre: 'Río Usumacinta',
      jerarquia: 2,
      area: '35,000 km²',
      importancia: 'Alta',
      municipios: 28,
      poblacion: '850K habitantes',
      color: '#ea580c',
      descripcion: 'Importante cuenca transfronteriza con Guatemala, rica en biodiversidad.'
    },
    {
      id: '3',
      nombre: 'Río Lacantún',
      jerarquia: 3,
      area: '18,500 km²',
      importancia: 'Alta',
      municipios: 12,
      poblacion: '320K habitantes',
      color: '#ca8a04',
      descripcion: 'Cuenca estratégica en la Selva Lacandona, clave para la conservación.'
    },
    {
      id: '4',
      nombre: 'Río Suchiate',
      jerarquia: 4,
      area: '8,200 km²',
      importancia: 'Media',
      municipios: 8,
      poblacion: '180K habitantes',
      color: '#16a34a',
      descripcion: 'Cuenca fronteriza con Guatemala, importante para el comercio regional.'
    },
    {
      id: '5',
      nombre: 'Río Coatán',
      jerarquia: 5,
      area: '6,800 km²',
      importancia: 'Media',
      municipios: 6,
      poblacion: '120K habitantes',
      color: '#2563eb',
      descripcion: 'Cuenca costera del Pacífico, relevante para la agricultura regional.'
    }
  ]

  const filteredCuencas = cuencas.filter(cuenca =>
    cuenca.nombre.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <motion.header 
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="bg-white shadow-lg border-b"
      >
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="flex items-center space-x-2 text-gray-600 hover:text-blue-600 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Volver al Inicio</span>
              </button>
              <div className="h-6 w-px bg-gray-300" />
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Droplets className="text-white w-4 h-4" />
                </div>
                <h1 className="text-xl font-bold text-gray-800">Plataforma de Cuencas</h1>
              </div>
            </div>
          </div>
        </div>
      </motion.header>

      <div className="container mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Panel de Control */}
          <motion.div 
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="lg:col-span-1 space-y-6"
          >
            {/* Búsqueda y filtros */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <Search className="w-5 h-5 mr-2 text-blue-600" />
                Búsqueda y Filtros
              </h2>
              <div className="space-y-4">
                <div>
                  <input
                    type="text"
                    placeholder="Buscar cuenca..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <button className="w-full flex items-center justify-center space-x-2 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors">
                  <Filter className="w-4 h-4" />
                  <span>Filtros Avanzados</span>
                </button>
              </div>
            </div>

            {/* Lista de Cuencas */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <BarChart3 className="w-5 h-5 mr-2 text-blue-600" />
                Jerarquía de Cuencas
              </h2>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {filteredCuencas.map((cuenca) => (
                  <motion.div
                    key={cuenca.id}
                    whileHover={{ scale: 1.02 }}
                    onClick={() => setSelectedCuenca(cuenca.id)}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedCuenca === cuenca.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-3">
                        <div 
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: cuenca.color }}
                        />
                        <span className="font-semibold text-gray-800">#{cuenca.jerarquia}</span>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        cuenca.importancia === 'Muy Alta' 
                          ? 'bg-red-100 text-red-800'
                          : cuenca.importancia === 'Alta'
                          ? 'bg-orange-100 text-orange-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {cuenca.importancia}
                      </span>
                    </div>
                    <h3 className="font-bold text-gray-800 mb-1">{cuenca.nombre}</h3>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p>Área: {cuenca.area}</p>
                      <p>Municipios: {cuenca.municipios}</p>
                      <p>Población: {cuenca.poblacion}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Mapa y Detalles */}
          <motion.div 
            initial={{ x: 50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="lg:col-span-2 space-y-6"
          >
            {/* Mapa Interactivo */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <MapPin className="w-5 h-5 mr-2 text-blue-600" />
                Mapa de Cuencas de Chiapas
              </h2>
              <div className="bg-gradient-to-br from-blue-100 to-green-100 rounded-lg h-96 flex items-center justify-center relative overflow-hidden">
                <div 
                  className="absolute inset-0 bg-cover bg-center opacity-60"
                  style={{
                    backgroundImage: 'url(https://images.pexels.com/photos/1435752/pexels-photo-1435752.jpeg?auto=compress&cs=tinysrgb&w=800)'
                  }}
                />
                <div className="relative z-10 text-center">
                  <MapPin className="w-16 h-16 text-blue-600 mx-auto mb-4" />
                  <p className="text-lg font-semibold text-gray-700 mb-2">Mapa Interactivo</p>
                  <p className="text-gray-600">Visualización de cuencas hidrográficas</p>
                </div>
                
                {/* Marcadores simulados */}
                {cuencas.map((cuenca, index) => (
                  <motion.div
                    key={cuenca.id}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.6 + index * 0.1 }}
                    className="absolute cursor-pointer"
                    style={{
                      left: `${20 + index * 15}%`,
                      top: `${30 + index * 10}%`
                    }}
                    onClick={() => setSelectedCuenca(cuenca.id)}
                  >
                    <div 
                      className="w-6 h-6 rounded-full border-2 border-white shadow-lg flex items-center justify-center"
                      style={{ backgroundColor: cuenca.color }}
                    >
                      <span className="text-white text-xs font-bold">{cuenca.jerarquia}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Detalles de Cuenca Seleccionada */}
            {selectedCuenca && (
              <motion.div 
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                className="bg-white rounded-xl shadow-lg p-6"
              >
                {(() => {
                  const cuenca = cuencas.find(c => c.id === selectedCuenca)
                  if (!cuenca) return null
                  
                  return (
                    <>
                      <div className="flex items-center justify-between mb-4">
                        <h2 className="text-xl font-bold text-gray-800 flex items-center">
                          <Info className="w-5 h-5 mr-2 text-blue-600" />
                          Detalles de la Cuenca
                        </h2>
                        <div 
                          className="w-6 h-6 rounded-full"
                          style={{ backgroundColor: cuenca.color }}
                        />
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <h3 className="text-2xl font-bold text-gray-800 mb-2">{cuenca.nombre}</h3>
                          <p className="text-gray-600 mb-4">{cuenca.descripcion}</p>
                          
                          <div className="space-y-3">
                            <div className="flex justify-between">
                              <span className="font-medium text-gray-700">Jerarquía:</span>
                              <span className="font-bold text-blue-600">#{cuenca.jerarquia}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="font-medium text-gray-700">Importancia:</span>
                              <span className={`font-bold ${
                                cuenca.importancia === 'Muy Alta' 
                                  ? 'text-red-600'
                                  : cuenca.importancia === 'Alta'
                                  ? 'text-orange-600'
                                  : 'text-yellow-600'
                              }`}>
                                {cuenca.importancia}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="space-y-4">
                          <div className="bg-gray-50 rounded-lg p-4">
                            <h4 className="font-semibold text-gray-800 mb-2">Datos Generales</h4>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span>Área total:</span>
                                <span className="font-medium">{cuenca.area}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Municipios:</span>
                                <span className="font-medium">{cuenca.municipios}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>Población:</span>
                                <span className="font-medium">{cuenca.poblacion}</span>
                              </div>
                            </div>
                          </div>
                          
                          <button className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors">
                            Ver Análisis Detallado
                          </button>
                        </div>
                      </div>
                    </>
                  )
                })()}
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  )
}

export default Platform
